
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, Shield, Users, Clock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/context/language-context';

const About = () => {
  const { t } = useLanguage();
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-pink-gradient text-gradient">{t('about.title')}</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('about.mission_title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('about.mission_content')}
            </p>
            <p className="text-gray-600 mb-6">
              Our platform is designed to empower escorts to work independently, safely, and on their own terms, while providing clients with access to verified, professional companions who offer exceptional experiences.
            </p>
            <div className="mt-8">
              <Button asChild className="bg-pink-gradient hover:opacity-90">
                <Link to="/register">Join Our Community</Link>
              </Button>
            </div>
          </div>
          <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-xl p-8">
            <h2 className="text-2xl font-bold mb-4">{t('about.why_choose')}</h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Shield className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">{t('about.safety_first')}</h3>
                  <p className="text-gray-600">All profiles are carefully verified to ensure the safety and security of our community.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Users className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">{t('about.quality_selection')}</h3>
                  <p className="text-gray-600">Only the finest escorts are featured on our platform, ensuring premium experiences.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Heart className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">{t('about.discreet_service')}</h3>
                  <p className="text-gray-600">Your privacy is our priority. All interactions and transactions are completely confidential.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Clock className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold">{t('about.support')}</h3>
                  <p className="text-gray-600">Our customer support team is available around the clock to assist with any queries.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <Card className="mb-16">
          <CardHeader>
            <CardTitle className="text-2xl">Our Commitment to You</CardTitle>
            <CardDescription>
              The Pink Paradise difference
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center text-center p-4">
                <div className="bg-pink-100 p-3 rounded-full mb-4">
                  <Shield className="h-8 w-8 text-pink-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Safety & Privacy</h3>
                <p className="text-gray-600">
                  We employ strict verification procedures and advanced privacy measures to keep both escorts and clients safe.
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="bg-purple-100 p-3 rounded-full mb-4">
                  <CheckCircle2 className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Quality Assurance</h3>
                <p className="text-gray-600">
                  Every profile undergoes thorough verification to ensure authenticity and the highest standards.
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4">
                <div className="bg-pink-100 p-3 rounded-full mb-4">
                  <Users className="h-8 w-8 text-pink-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Respectful Community</h3>
                <p className="text-gray-600">
                  We foster a community built on mutual respect, consent, and professionalism between all members.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Ready to Discover?</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Join Pink Paradise today and experience the difference. Whether you're seeking companionship or offering your services, our platform provides the perfect environment.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="bg-pink-gradient hover:opacity-90">
              <Link to="/register">Create an Account</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/explore">Explore Profiles</Link>
            </Button>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-600 hover:text-primary">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-600 hover:text-primary">Blog</Link></li>
                <li><Link to="/careers" className="text-gray-600 hover:text-primary">Careers</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/support" className="text-gray-600 hover:text-primary">Contact Us</Link></li>
                <li><Link to="/faq" className="text-gray-600 hover:text-primary">FAQ</Link></li>
                <li><Link to="/safety-tips" className="text-gray-600 hover:text-primary">Safety Tips</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-600 hover:text-primary">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-600 hover:text-primary">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="text-gray-600 hover:text-primary">Cookie Policy</Link></li>
                <li><Link to="/content-policy" className="text-gray-600 hover:text-primary">Content Policy</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><Link to="/how-it-works" className="text-gray-600 hover:text-primary">How It Works</Link></li>
                <li><Link to="/security" className="text-gray-600 hover:text-primary">Security</Link></li>
                <li><Link to="/pricing" className="text-gray-600 hover:text-primary">Pricing</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default About;
